import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { cn } from "../lib/utils";
import { Typography } from "./typography";

/**
 * Put this on the card's action (button or link) so its hit area stretches
 * over the whole card, making the entire card clickable.
 */
export const contentCardActionClassName =
  "after:absolute after:inset-0 after:rounded-lg after:content-['']";

const contentCardVariants = cva(
  "group relative flex h-full w-full flex-col overflow-hidden rounded-lg border-2 border-transparent",
  {
    variants: {
      layout: {
        centered: "items-center justify-center gap-4 p-8 text-center md:p-12",
        "media-top": "text-left",
      },
      tone: {
        none: "bg-transparent",
        white: "bg-scheme-card-white",
        beige: "bg-scheme-card-beige",
        "dark-beige": "bg-scheme-card-dark-beige",
        accent: "bg-scheme-card-accent",
        dark: "bg-scheme-card-dark",
      },
      interactive: {
        true: "hover:border-scheme-accent-soft transition-colors",
        false: "",
      },
    },
    defaultVariants: { layout: "centered", tone: "white", interactive: false },
  },
);

const bodyVariants = cva("flex flex-1 flex-col", {
  variants: {
    layout: {
      centered: "contents",
      "media-top": "gap-1 p-6",
    },
    flush: {
      true: "px-0 pb-0 pt-4",
      false: "",
    },
  },
  defaultVariants: { layout: "centered", flush: false },
});

export type ContentCardTone =
  | "none"
  | "white"
  | "beige"
  | "dark-beige"
  | "accent"
  | "dark";

export type ContentCardLayout = "centered" | "media-top";

export interface ContentCardProps {
  /**
   * Visual above the title. `centered`: rendered at a fixed 64px height.
   * `media-top`: fills the card edge to edge in a 4:3 frame (pass an image
   * that covers its box, e.g. next/image with `fill`).
   */
  media?: ReactNode;
  title?: string;
  description?: ReactNode;
  /** The card's single action; give it `contentCardActionClassName`. Without it the card is static (no hover). */
  action?: ReactNode;
  /** Card background, from the scheme's card colors. `none` blends into the surface behind it. */
  tone?: ContentCardTone;
  /** `centered`: small media, centered text. `media-top`: edge-to-edge photo, left-aligned text, action pinned to the bottom. */
  layout?: ContentCardLayout;
}

/**
 * ContentCard — media, title, description, one action. The whole card is
 * clickable (the action's hit area stretches over it). Background comes from
 * the scheme's card colors via `tone`.
 */
export function ContentCard({
  media,
  title,
  description,
  action,
  tone = "white",
  layout = "centered",
}: ContentCardProps) {
  const textColor = tone === "dark" ? "inverse" : "primary";
  const isMediaTop = layout === "media-top";

  return (
    <div
      className={cn(
        contentCardVariants({ layout, tone, interactive: !!action }),
      )}
    >
      {media &&
        (isMediaTop ? (
          <div
            className={cn(
              "relative aspect-[4/3] w-full shrink-0 overflow-hidden",
              tone === "none" && "rounded-lg",
            )}
          >
            {media}
          </div>
        ) : (
          <div className="flex h-16 items-center">{media}</div>
        ))}
      <div
        className={cn(
          bodyVariants({ layout, flush: isMediaTop && tone === "none" }),
        )}
      >
        {(title || description) && (
          <div className="flex flex-col gap-1">
            {title && (
              <Typography
                as="h2"
                variant={isMediaTop ? "h6" : "h3"}
                color={textColor}
              >
                {title}
              </Typography>
            )}
            {description && (
              <Typography as="div" variant="body" color={textColor}>
                {description}
              </Typography>
            )}
          </div>
        )}
        {action && (
          <div className={isMediaTop ? "mt-auto pt-4" : "pt-2"}>{action}</div>
        )}
      </div>
    </div>
  );
}
